import { datadogRum } from '@datadog/browser-rum';
import countries from '@trm/shared/data/countries';
import { cx } from '@trm/ui-kit-admin/v7/utils';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import logo from '~/assets/images/auth/topstep_logo_new.svg';
import FormCountrySelect from '~/components/FormCountrySelect/FormCountrySelect';
import FormRegionSelect from '~/components/FormRegionSelect/FormRegionSelect';
import {
  AddressValidationErrorModal,
  SuggestedAddressModalNew,
} from '~/components/modals/GoogleAddressValidation';
import { RedirectingModal } from '~/components/modals/LegacyRedirect/LegacyRedirectModal';
import useAuth from '~/hooks/useAuth';
import {
  AddressProps,
  AddressVerificationStatus,
  ErrorDetails,
  UNSUPPORTED_COUNTRY_ERROR_KEY,
  ValidationResponse,
  mapPostalAddressToFormik,
  useAddressValidation,
} from '~/hooks/useGoogleAddressValidation';
import { useLegacyRedirect } from '~/hooks/useLegacyRedirect';
import useRecaptchaValidation from '~/hooks/useRecaptchaValidation';
import { OnboardingButton } from '~/pages/auth/onboarding/components/OnboardingButton/OnboardingButton';
import { OnboardingInput } from '~/pages/auth/onboarding/components/OnboardingInput/OnboardingInput';
import { EMAIL_REGEX } from '~/utils/email-validation';
import { useNavigation } from '~/utils/nav';
import { setOnboardingSession } from '~/utils/onboarding';

/**
 * Props interface for the registration form values.
 * Used by AuthRegisterCognitoNew for Cognito-based registration flow.
 */
export interface RegisterFormProps {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  region: string;
  zip: string;
  country: string;
  submit: string | null;
  addressVerification: AddressVerificationStatus | undefined;
}

const getInitialValues = (email?: string): RegisterFormProps => ({
  email: email || '',
  username: '',
  firstName: '',
  lastName: '',
  street: '',
  city: '',
  region: '',
  zip: '',
  country: '',
  submit: null,
  addressVerification: undefined,
});

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .max(255)
    .matches(EMAIL_REGEX, 'Must be a valid email address'),
  username: Yup.string()
    .matches(/^[a-zA-Z0-9]+$/, 'Username can only contain letters and numbers')
    .min(4, 'Username is too short')
    .required('Username is required'),
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  street: Yup.string().min(4, 'Address too short.').required('Address is required'),
  city: Yup.string().required('City is required'),
  country: Yup.string().max(2, 'Use 2 Character Country Code').required('Country Code is required'),
  region: Yup.string().when('country', {
    is: (country: string) => {
      return !countries.find((c) => c.code === country)?.noRegions;
    },
    then: (schema) => schema.required('State / Province is required'),
    otherwise: (schema) => schema,
  }),
  /**
   * Zip/Postal code validation:
   * - US: Required, 5 digits or ZIP+4 format (e.g., "90210", "10001", "12345-6789")
   * - Non-US: No validation (optional, any format)
   */
  zip: Yup.string().when('country', {
    is: (val: string) => val && val.toUpperCase() === 'US',
    then: (schema) =>
      schema
        .required('Must enter Zip Code')
        .matches(
          /^\d{5}(-\d{4})?$/,
          'Zip code must be 5 digits or ZIP+4 format (e.g., 12345-6789)',
        ),
  }),
});

/**
 * Registration form component for new user account creation using AWS Cognito authentication.
 *
 * This component handles the complete user registration flow including form validation,
 * address verification, reCAPTCHA validation, and account creation via Cognito.
 *
 * ## Business Rules
 *
 * **Required Fields:**
 * - Email address (validated format)
 * - Username (alphanumeric, 4+ characters)
 * - First name and last name
 * - Street address (4+ characters)
 * - City
 * - Country (2-character code)
 *
 * **Conditional Required Fields:**
 * - State/Province: Required for US and Canada
 * - Zip/Postal Code: Required for US addresses (numeric only)
 *
 * **Address Validation:**
 * - Uses Google Address Validation API for US addresses
 * - Suggests corrected addresses when available
 * - Allows users to proceed with entered address if validation fails
 * - Tracks verification status: 'verified', 'unverified', or 'unsupported'
 *
 * **Security:**
 * - reCAPTCHA v3 protection against bots
 * - Duplicate email/username detection
 * - Legacy user migration handling
 *
 * ## Navigation & Routing
 *
 * **Entry Points:**
 * - Standard flow: `/register` (when social login is disabled)
 * - Pre-filled email: Accepts email from `location.state.email` when navigating from `/register`
 *
 * **Exit Routes:**
 * - Success: Redirects to `/verify-email` (with user data in sessionStorage)
 * - Legacy user: Redirects to legacy system via `useLegacyRedirect` hook
 * - Cancel: User navigates back to login via header link
 *
 * ## Component Props
 *
 * - `location.state.email` (from router): Pre-filled email from social login landing page
 *
 * ## Post-Registration Flow
 *
 * On successful registration:
 * 1. User account created in Cognito
 * 2. User data stored in sessionStorage via `setOnboardingSession()`
 * 3. Redirects to `/verify-email`
 * 4. Confirmation email sent (handled by backend)
 *
 * ## Error Handling
 *
 * - **Duplicate Email**: Shows inline error, focuses email field
 * - **Duplicate Username**: Shows inline error, focuses username field
 * - **Address Validation Failed**: Shows modal with error details or suggestions
 * - **Network/Server Errors**: Shows general error message, logs to Datadog
 * - **Legacy User**: Triggers modal explaining migration, redirects to legacy system
 *
 * @example
 * ```tsx
 * import AuthRegisterCognitoNew from '~/sections/auth/auth-forms/AuthRegisterCognitoNew';
 *
 * // Standard usage (mounts inside AuthWrapperRegisterNew)
 * <AuthWrapperRegisterNew>
 *   <AuthRegisterCognitoNew />
 * </AuthWrapperRegisterNew>
 * ```
 *
 * @see {@link RegisterFormProps} for form field types
 * @see {@link useAddressValidation} for address validation logic
 * @see {@link useLegacyRedirect} for legacy user migration handling
 *
 * @returns React component rendering the registration form
 */
const AuthRegisterCognitoNew = () => {
  const { search, navigate: navigateWithSearch } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const hasSetVariant = useRef(false);

  // Add query param for analytics tracking (Heap)
  useEffect(() => {
    if (search.variant === 'new') {
      hasSetVariant.current = true;
      return;
    }
    if (!hasSetVariant.current) {
      hasSetVariant.current = true;
      navigateWithSearch({ search: { variant: 'new' } });
    }
  }, [search.variant, navigateWithSearch]);

  // Get email from location state (from /register navigation)
  const initialEmail = (location.state as { email?: string })?.email;
  const { validateAddress, error, isValidating } = useAddressValidation();
  const [showSuggestedModal, setShowSuggestedModal] = useState(false);
  const [addressValidationResult, setAddressValidationResult] = useState<ValidationResponse | null>(
    null,
  );
  const [addressInput, setAddressInput] = useState<AddressProps | null>(null);
  const [showAddressValidationErrorModal, setShowAddressValidationErrorModal] = useState(false);
  const [validationAddressErrors, setValidationAddressErrors] = useState<
    ErrorDetails[] | undefined
  >([]);
  const [useEnteredAddress, setUseEnteredAddress] = useState(false);
  const { getToken, isReady } = useRecaptchaValidation(process.env.RECAPTCHA_SITE_KEY!);
  const { showLegacyModal, triggerLegacyRedirect } = useLegacyRedirect();

  const scrollAndFocus = (name: string) => {
    const input = document.querySelector(`input[name="${name}"]`) as HTMLInputElement | null;
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    input.focus({ preventScroll: true });
  };

  const handleOnSubmit = async (
    values: RegisterFormProps,
    { setErrors, setStatus, setSubmitting }: FormikHelpers<RegisterFormProps>,
  ) => {
    try {
      if (!isReady) {
        setErrors({
          submit: 'Security check not ready. Please wait a moment and try again.',
        });
        setSubmitting(false);
        return;
      }

      const recaptchaToken = await getToken('signup');

      if (!recaptchaToken) {
        setErrors({
          submit: 'Security check not ready. Please wait a moment and try again.',
        });
        setSubmitting(false);
        return;
      }

      const addressInput = {
        regionCode: values.country,
        locality: values.city,
        administrativeArea: values.region,
        postalCode: values.zip,
        addressLines: [values.street],
      };
      setAddressInput(addressInput);

      let addressVerificationStatus = values?.addressVerification;
      try {
        const { resultAddressValidation, validation } = await validateAddress({
          address: addressInput,
        });

        if (validation?.status === UNSUPPORTED_COUNTRY_ERROR_KEY) {
          addressVerificationStatus = 'unsupported';
        }
        if (error && !isValidating) {
          setErrors({ submit: 'Something went wrong validating your address' });
          setSubmitting(false);
          return;
        }

        if (!validation.isValid && !useEnteredAddress) {
          if (validation.shouldSuggestModal) {
            setAddressValidationResult(resultAddressValidation || null);
            setShowSuggestedModal(true);
          } else {
            setShowAddressValidationErrorModal(true);
            setValidationAddressErrors(validation.errorDetails);
          }
          setSubmitting(false);
          return;
        }

        if (!useEnteredAddress) {
          addressVerificationStatus = 'verified';
        }
      } catch (err) {
        setErrors({ submit: 'Something went wrong validating your address.' });
        setSubmitting(false);
        return;
      }

      await register({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        street: values.street,
        city: values.city,
        region: values.region,
        zip: values.zip,
        country: values.country,
        screenName: values.username,
        recaptchaToken,
        addressVerification: addressVerificationStatus,
      });

      setStatus({ success: true });

      // Store email and name in sessionStorage for email verification
      const fullName = `${values.firstName} ${values.lastName}`.trim();
      setOnboardingSession(values.email, fullName);

      navigate('/verify-email', { replace: true });
    } catch (err: unknown) {
      setStatus({ success: false });
      const errorMessage =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message || (err instanceof Error ? err.message : 'An unexpected error occurred');
      switch (errorMessage) {
        case 'User with this email already exists':
          setErrors({ email: 'A user with this email already exists.' });
          scrollAndFocus('email');
          break;

        case 'Screen name already taken':
          setErrors({ username: 'This username is already taken.' });
          scrollAndFocus('username');
          break;

        case 'legacy-email':
          triggerLegacyRedirect();
          return;

        default:
          const reportError =
            err instanceof Error ? err : new Error(`Registration failed: ${errorMessage}`);
          datadogRum.addError(reportError, {
            message: errorMessage,
            errorType: 'auth_error',
            operation: 'registration_submit',
            context: 'registration',
          });
          setErrors({ submit: errorMessage });
          break;
      }
      setSubmitting(false);
    }
  };

  return (
    <Formik<RegisterFormProps>
      initialValues={getInitialValues(initialEmail)}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnChange
      validateOnBlur
      validateOnMount
    >
      {({
        errors,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        setFieldValue,
        touched,
        values,
      }) => {
        const isStateDisabled =
          values.country === '' ||
          countries.find((country) => country.code === values.country)?.noRegions;

        const confirmAddress = (addressVerificationStatus: AddressVerificationStatus) => {
          setUseEnteredAddress(true);
          setShowSuggestedModal(false);
          setShowAddressValidationErrorModal(false);
          setFieldValue('addressVerification', addressVerificationStatus);
          handleSubmit();
        };

        const handleFieldChange = (field: string, value: string) => {
          setUseEnteredAddress(false);
          setFieldValue(field, value);
        };

        // Extract region field configuration to avoid nested ternaries
        const isUSOrCA = values.country === 'US' || values.country === 'CA';
        const isUS = values.country === 'US';
        const regionLabel = isUS ? 'State' : 'Province';
        const regionPlaceholder = isUS ? 'Select state...' : 'Select province...';

        return (
          <Form className="flex min-h-0 flex-1 flex-col">
            {/* Header — always visible, never scrolls */}
            <div className="mb-4 shrink-0 px-4 pt-8 text-center text-white sm:mb-6 sm:px-8 sm:pt-8 sm:text-start md:px-12 md:pt-12">
              <img
                src={logo}
                alt="Topstep logo"
                className="mx-auto mb-6 h-8 w-auto sm:mx-0 lg:hidden"
              />
              <h1 className="mb-2 text-2xl font-bold">Set up your trader account</h1>
              <p className="text-sm text-gray-300">
                Already a Topstep Trader?{' '}
                <Link to="/login" className="font-medium text-utility-blue-500 underline">
                  Login
                </Link>
              </p>
            </div>

            {/* Content — scrollable when form exceeds viewport */}
            <div className="min-h-0 flex-1 overflow-y-auto border-t border-b border-secondary px-4 py-4 sm:px-8 sm:py-6 md:px-12">
              <div className="space-y-4 sm:space-y-6">
                {/* Email */}
                <OnboardingInput
                  id="email-input"
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter Email Address"
                  value={values.email}
                  onChange={(value) => setFieldValue('email', value)}
                  onBlur={handleBlur}
                  isDisabled={isSubmitting}
                  isInvalid={!!(errors.email && touched.email)}
                  hint={errors.email && touched.email ? errors.email : undefined}
                  size="md"
                />
                {/* Username */}
                <OnboardingInput
                  id="username-input"
                  type="text"
                  name="username"
                  label="Username"
                  placeholder="Choose Username"
                  value={values.username}
                  onChange={(value) => setFieldValue('username', value)}
                  onBlur={handleBlur}
                  isDisabled={isSubmitting}
                  isInvalid={!!(errors.username && touched.username)}
                  hint={errors.username && touched.username ? errors.username : undefined}
                  size="md"
                />
                <div className="mt-6 border-t border-gray-800" />
                <div className="pt-2">
                  <h3 className="mb-1 text-base font-semibold text-white">
                    Tell us about yourself
                  </h3>
                  <p className="mb-4 text-sm text-gray-300">
                    Use the legal name on your IDs (e.g., driver's license, passport).
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <OnboardingInput
                    id="firstName-input"
                    type="text"
                    name="firstName"
                    label="Legal First Name"
                    value={values.firstName}
                    onChange={(value) => setFieldValue('firstName', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.firstName && touched.firstName)}
                    hint={errors.firstName && touched.firstName ? errors.firstName : undefined}
                    size="md"
                  />
                  <OnboardingInput
                    id="lastName-input"
                    type="text"
                    name="lastName"
                    label="Legal Last Name"
                    value={values.lastName}
                    onChange={(value) => setFieldValue('lastName', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.lastName && touched.lastName)}
                    hint={errors.lastName && touched.lastName ? errors.lastName : undefined}
                    size="md"
                  />
                </div>
                {/* Country */}
                <FormCountrySelect
                  label="Country"
                  name="country"
                  placeholder="Select country"
                  value={values.country}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  error={errors.country}
                  touched={touched.country}
                  disabled={isSubmitting}
                  className="border-gray-600"
                  popoverClassName="bg-gray-950"
                  inputClassName="bg-gray-950"
                />
                {/* Street Address */}
                <OnboardingInput
                  id="street-input"
                  type="text"
                  name="street"
                  label="Street Address"
                  value={values.street}
                  onChange={(value) => handleFieldChange('street', value)}
                  onBlur={handleBlur}
                  isDisabled={isSubmitting}
                  isInvalid={!!(errors.street && touched.street)}
                  hint={errors.street && touched.street ? errors.street : undefined}
                  size="md"
                />
                {/* City, Zip Code, State */}
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
                  <OnboardingInput
                    id="city-input"
                    type="text"
                    name="city"
                    label="City"
                    value={values.city}
                    onChange={(value) => handleFieldChange('city', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.city && touched.city)}
                    hint={errors.city && touched.city ? errors.city : undefined}
                    size="md"
                  />
                  <OnboardingInput
                    id="zip-input"
                    type="text"
                    name="zip"
                    label="Zip Code"
                    value={values.zip}
                    onChange={(value) => handleFieldChange('zip', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.zip && touched.zip)}
                    hint={errors.zip && touched.zip ? errors.zip : undefined}
                    size="md"
                  />
                  <div className="col-span-2 sm:col-span-1">
                    {isUSOrCA ? (
                      <FormRegionSelect
                        label={regionLabel}
                        country={values.country}
                        name="region"
                        placeholder={regionPlaceholder}
                        value={values.region}
                        onChange={handleFieldChange}
                        onBlur={handleBlur}
                        error={errors.region}
                        touched={touched.region}
                        disabled={isSubmitting || isStateDisabled}
                        className="border-gray-600"
                        popoverClassName="bg-gray-950"
                        inputClassName="bg-gray-950"
                      />
                    ) : (
                      <OnboardingInput
                        id="region-input"
                        type="text"
                        name="region"
                        label="State / Region"
                        placeholder="Enter state / region"
                        value={values.region}
                        onChange={(value) => handleFieldChange('region', value)}
                        onBlur={handleBlur}
                        isDisabled={isSubmitting || isStateDisabled}
                        isInvalid={!!(errors.region && touched.region)}
                        hint={errors.region && touched.region ? errors.region : undefined}
                        size="md"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer — always visible, never scrolls */}
            <div className="shrink-0 space-y-4 px-4 py-4 sm:px-8 sm:pt-6 md:px-12 md:pt-6 md:pb-12">
              {errors.submit && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{errors.submit}</p>
                </div>
              )}
              <OnboardingButton
                type="submit"
                size="lg"
                isDisabled={isSubmitting || !isValid}
                isLoading={isSubmitting}
                className={cx('w-full')}
              >
                Continue
              </OnboardingButton>
              <p className="text-sm text-gray-300">
                By signing up, you agree to our{' '}
                <a
                  href="https://www.topstep.com/terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-utility-blue-500 underline"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href="https://www.topstep.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-utility-blue-500 underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            {/* Modals */}
            <SuggestedAddressModalNew
              open={showSuggestedModal}
              suggestedAddress={addressValidationResult?.result?.address?.postalAddress || null}
              longitude={addressValidationResult?.result?.geocode?.location.longitude}
              latitude={addressValidationResult?.result?.geocode?.location.latitude}
              userAddress={addressInput}
              onConfirm={(selected: AddressProps | null, selectedOption: 'user' | 'suggested') => {
                if (selected) {
                  mapPostalAddressToFormik(selected, setFieldValue);
                  const addressVerificationStatus =
                    selectedOption === 'suggested' ? 'verified' : 'overridden';
                  confirmAddress(addressVerificationStatus);
                }
              }}
              onClose={() => {
                setShowSuggestedModal(false);
              }}
            />
            <AddressValidationErrorModal
              userAddress={addressInput}
              open={showAddressValidationErrorModal}
              errors={validationAddressErrors || []}
              onUseCurrent={() => {
                confirmAddress('overridden');
              }}
              onClose={() => {
                setUseEnteredAddress(false);
                setShowAddressValidationErrorModal(false);
              }}
            />
            {showLegacyModal && (
              <RedirectingModal
                open={showLegacyModal}
                title="You're already registered!"
                messageLines={[
                  'It looks like this email is already associated with a Topstep account.',
                  "We're redirecting you to the correct place...",
                ]}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default AuthRegisterCognitoNew;
